/* storybook-check-ignore */
import { useState } from 'react';
import { Flex, Box, Text, Input, Button, InputProps } from '@opendoor/bricks/core';

import { CheckList, CheckListItem } from 'shared/components/Checklists';
import { PhoneNumberPrivacyPolicy } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/PhoneNumberPrivacyPolicy';
import { ExteriorAssessmentScheduleContent } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/ExteriorAssessmentScheduleContent';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useUpdateLeadPhoneNumber } from 'sellDirect/graphql/useUpdateLeadPhoneNumber';
import { useSSEAContext } from 'sellDirect/SSEAContext';
import { NoAvailableSlots } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/NoAvailableSlots';

type PhoneNumberInputContent = {
  onChangePhoneNumber: InputProps['onChange'];
  phoneNumber?: string;
  isPhoneNumberValid?: boolean;
};

const CHECKLIST: Array<CheckListItem> = [
  {
    title: "What's this?",
    body: 'A member of our team will visit your home and review the exterior.',
  },
  {
    title: 'Why should I do it?',
    body: 'Once you complete the assessment, we’ll have what we need to prepare your final offer.',
  },
  {
    title: 'How do I prepare?',
    body: "You don't have to be home, and you only need to provide access to the backyard.",
  },
];

const AGENT_CHECKLIST: Array<CheckListItem> = [
  {
    title: "What's this?",
    body: "A member of our team will visit your client's home and review the exterior.",
  },
  {
    title: 'Why should my client do it?',
    body: "Once you complete the assessment, we’ll have what we need to prepare your client's final offer.",
  },
  {
    title: 'How can my client prepare?',
    body: 'No one needs to be home. Your client only needs to provide access to the backyard. Let your client know about the exterior assessment, including the date and relevant information.',
  },
];

const IN_PERSON_CHECKLIST: Array<CheckListItem> = [
  {
    title: "What's this?",
    body: 'A member of our team will visit your home and review the exterior. They will also take a quick look inside, so you or another adult (18 years or older) will need to be home. This typically takes 30 minutes.',
  },
  {
    title: 'Why should I do it?',
    body: 'Once you complete the assessment, we’ll have what we need to prepare your final offer.',
  },
  {
    title: 'How do I prepare?',
    body: 'You don’t need to do anything special to prepare, but you will need to be home to let one of our experts come take a look.',
  },
];

const IN_PERSON_AGENT_CHECKLIST: Array<CheckListItem> = [
  {
    title: "What's this?",
    body: 'A member of our team will visit your client’s home and review the exterior. They will take a quick look inside so either you, your client, or another adult (18 years or older) will need to be home or provide us with an access code to enter the home. This typically takes 30 minutes.',
  },
  {
    title: 'Why should my client do it?',
    body: "Once you complete the assessment, we’ll have what we need to prepare your client's final offer.",
  },
  {
    title: 'How can my client prepare?',
    body: 'Your client does not need to clean, stage, or prepare the home in any special way. Our team member will need access to the inside of the home and backyard. Let your client know about the home assessment, including the date and relevant information.',
  },
];

const CONFIRM_BTN_TEXT = 'Confirm';
const CANCEL_BTN_TEXT = 'Cancel';

/** TODO: Add documentation */
export const ScheduleSection: React.FC<{
  shouldSeeAgentExperience?: boolean;
  shouldHidePhoneNumberContent?: boolean;
  isInPersonSseaEnabled?: boolean;
}> = ({ shouldSeeAgentExperience, shouldHidePhoneNumberContent, isInPersonSseaEnabled }) => {
  const { lead } = useQualificationContext();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(
    lead?.customerV2?.primaryPhoneNumber?.number?.value,
  );
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean | undefined>(true);

  const {
    currentInspectionDate,
    canSaveSelectedDate,
    saveSelectedDate,
    cancelEA,
    availableExteriorAssessmentSlots,
  } = useSSEAContext();
  const { updateLeadCustomerPhoneNumber } = useUpdateLeadPhoneNumber();

  const onChangePhoneNumber: InputProps['onChange'] = (_event, value, isValidFormat) => {
    setPhoneNumber(value);
    setIsPhoneNumberValid(isValidFormat);
  };

  let checklistItems = shouldSeeAgentExperience ? AGENT_CHECKLIST : CHECKLIST;

  if (isInPersonSseaEnabled) {
    checklistItems = shouldSeeAgentExperience ? IN_PERSON_AGENT_CHECKLIST : IN_PERSON_CHECKLIST;
  }

  const onClickConfirm = async () => {
    try {
      await saveSelectedDate();
    } finally {
      // we don't want the phone number update to be block as HPM can do EA w/o seller present or at home
      // we also send out email comms
      phoneNumber && (await updateLeadCustomerPhoneNumber(phoneNumber));
    }
  };

  const renderPhoneNumberContent = ({
    onChangePhoneNumber,
    phoneNumber,
  }: PhoneNumberInputContent) => {
    return (
      <>
        <Input
          data-testid="ea-phone-number-input"
          analyticsName="cake-schedule-section-reminder-phone-number"
          id="schedule-ea"
          name="schedule-ea"
          aria-label="We'll text a reminder to your phone number"
          type="tel"
          value={phoneNumber}
          onChange={onChangePhoneNumber}
          unboundedWidth
          label={
            <Text fontSize="s1" lineHeight="s0" fontWeight="semibold">
              We'll text you a reminder to
            </Text>
          }
          autoComplete="tel"
        />
        <PhoneNumberPrivacyPolicy />
      </>
    );
  };

  return (
    <>
      {availableExteriorAssessmentSlots.length === 0 ? (
        <NoAvailableSlots shouldSeeAgentExperience={shouldSeeAgentExperience} />
      ) : (
        <ExteriorAssessmentScheduleContent />
      )}
      <Box pt={7}>
        <>
          {!shouldHidePhoneNumberContent &&
            renderPhoneNumberContent({ onChangePhoneNumber, phoneNumber, isPhoneNumberValid })}
          <Flex justifyContent="space-between" alignItems="center" width="100%" py={[4, 6]}>
            <Box mr={5} width="50%">
              <Button
                analyticsName="cake-cancel-exterior-assessment"
                aria-label="Cancel exterior assessment"
                onClick={cancelEA}
                variant="primary"
                disabled={!currentInspectionDate}
                // @ts-expect-error Bricks intentionally doesn't allow customization of border radii on buttons. Product teams should resolve this violation
                borderRadius="full"
                fontWeight="semibold"
              >
                {CANCEL_BTN_TEXT}
              </Button>
            </Box>
            <Box width="50%">
              <Button
                analyticsName="cake-confirm-exterior-assessment"
                aria-label="Confirm exterior assessment"
                onClick={onClickConfirm}
                variant="primary"
                fontWeight="semibold"
                // @ts-expect-error Bricks intentionally doesn't allow customization of border radii on buttons. Product teams should resolve this violation
                borderRadius="full"
                disabled={!canSaveSelectedDate}
              >
                {CONFIRM_BTN_TEXT}
              </Button>
            </Box>
          </Flex>
        </>
        <CheckList list={checklistItems} />
      </Box>
    </>
  );
};
